import {AlertCircleIcon, AlertTriangleIcon, ClockIcon, TrendingUpIcon, UsersIcon} from "lucide-react";
import {useMemo} from "react";

import {
    buildComboChart,
    buildBarChart,
    SEMANTIC_COLORS,
} from "@components/charts/echarts-toolkit";
import {EChartsCard} from "@components/charts/EChartsCard";
import {Badge} from "@components/ui/badge";
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@components/ui/card";
import {Skeleton} from "@components/ui/skeleton";
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from "@components/ui/table";
import {useVNextAR} from "@hooks/use-vnext";

import {ExecutiveSummary} from "./ExecutiveSummary";

interface ARTabProps {
    workspaceId: string;
    scenarioId: string;
    actualMonths?: number;
    forecastMonths?: number;
    period?: string;
    selectedMonth?: string;
    customFrom?: string;
    customTo?: string;
    fyStartMonth?: number;
    currentMonth?: string;
}

function fmt(n: number): string {
    return `$${n.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}`;
}

const AGING_COLORS: Record<string, string> = {
    current: "#10B981",
    "1-30": "#f59e0b",
    "31-60": "#f97316",
    "61-90": "#DC2626",
    "90+": "#991b1b",
};

const BUCKET_LABELS: Record<string, string> = {
    current: "Current",
    "1-30": "1-30 days",
    "31-60": "31-60 days",
    "61-90": "61-90 days",
    "90+": "90+ days",
};

function overduePctColor(pct: number): string {
    if (pct <= 10) return "text-green-600";
    if (pct <= 25) return "text-yellow-600";
    return "text-red-600";
}

function daysOverdueColor(days: number): string {
    if (days <= 30) return "text-yellow-600";
    if (days <= 60) return "text-orange-600";
    if (days <= 90) return "text-red-600";
    return "text-red-800 font-semibold";
}

function collectionRateColor(rate: number): string {
    if (rate >= 90) return "text-green-600";
    if (rate >= 70) return "text-yellow-600";
    return "text-red-600";
}

function hhiLabel(hhi: number): {text: string; color: string} {
    if (hhi > 2500) return {text: "Highly Concentrated", color: "text-red-600"};
    if (hhi >= 1500) return {text: "Moderate", color: "text-yellow-600"};
    return {text: "Low", color: "text-green-600"};
}

export function ARTab({workspaceId, scenarioId, period, selectedMonth, customFrom, customTo, fyStartMonth, currentMonth}: ARTabProps) {
    const {data, isLoading, error} = useVNextAR(workspaceId, undefined, customFrom, customTo);

    // ── Hero: Aging stacked bar (horizontal, one bar per bucket) ──
    const agingBarOption = useMemo(() => {
        if (!data?.aging?.length) return null;
        const buckets = data.aging.filter((b) => b.total_due > 0);
        if (!buckets.length) return null;
        return buildBarChart({
            categories: buckets.map((b) => BUCKET_LABELS[b.bucket] ?? b.bucket),
            series: [{
                key: "amount",
                label: "Amount Due",
                data: buckets.map((b) => b.total_due),
                color: undefined as unknown as string, // per-item colors below
            }],
            yFormat: "compact_currency",
            horizontal: true,
            barMaxWidth: 28,
            borderRadius: 4,
        });
    }, [data?.aging]);

    // Override bar colors per bucket (aging gradient)
    const agingBarPatched = useMemo(() => {
        if (!agingBarOption || !data?.aging) return null;
        const buckets = data.aging.filter((b) => b.total_due > 0);
        const series = (agingBarOption.series as Record<string, unknown>[]);
        if (series[0]) {
            series[0].data = buckets.map((b) => ({
                value: b.total_due,
                itemStyle: {color: AGING_COLORS[b.bucket] ?? "#94A3B8"},
            }));
            series[0].itemStyle = undefined;
        }
        return {...agingBarOption, color: undefined};
    }, [agingBarOption, data?.aging]);

    // ── Secondary: Monthly collection trend (combo) ──
    const monthlyComboOption = useMemo(() => {
        if (!data?.monthly_collection_trend?.length) return null;
        return buildComboChart({
            categories: data.monthly_collection_trend.map((m) => m.month),
            barSeries: [
                {
                    key: "invoiced",
                    label: "Invoiced",
                    data: data.monthly_collection_trend.map((m) => m.total_invoiced),
                    color: SEMANTIC_COLORS.primary,
                },
                {
                    key: "collected",
                    label: "Collected",
                    data: data.monthly_collection_trend.map((m) => m.total_collected),
                    color: SEMANTIC_COLORS.positive,
                },
            ],
            lineSeries: [
                {
                    key: "rate",
                    label: "Collection Rate %",
                    data: data.monthly_collection_trend.map((m) => m.collection_rate_pct),
                    color: SEMANTIC_COLORS.accent,
                    yAxisIndex: 1,
                },
            ],
            yFormat: "compact_currency",
            y2Format: "percent",
        });
    }, [data?.monthly_collection_trend]);

    // ── Loading / Error ──

    if (isLoading) {
        return (
            <div className="grid gap-4 md:grid-cols-4">
                {[1, 2, 3, 4].map((i) => (
                    <Card key={i}>
                        <CardHeader className="pb-2">
                            <Skeleton className="h-4 w-20" />
                            <Skeleton className="h-8 w-32 mt-1" />
                        </CardHeader>
                        <CardContent>
                            <Skeleton className="h-4 w-24" />
                        </CardContent>
                    </Card>
                ))}
            </div>
        );
    }

    if (error) {
        return (
            <Card>
                <CardContent className="p-6 text-center text-red-600">
                    <AlertCircleIcon className="size-5 inline mr-2" />
                    {error}
                </CardContent>
            </Card>
        );
    }

    if (!data) return null;

    const kpis = data.kpis ?? {
        total_ar: 0, dso: 0, overdue_total: 0, overdue_pct: 0,
        collection_rate: 0, avg_invoice_value: 0, invoice_count: 0,
        weighted_avg_days_to_pay: 0,
    };
    const topCustomers = data.top_customers ?? [];
    const pastDue = data.past_due ?? [];
    const concentration = data.concentration ?? {hhi: 0, top_1_pct: 0, top_5_pct: 0, top_10_pct: 0};
    const hhi = hhiLabel(concentration.hhi);

    return (
        <div className="flex-1 min-h-0 min-w-0 overflow-y-auto space-y-6">
            {/* Executive Summary */}
            <ExecutiveSummary workspaceId={workspaceId} scenarioId={scenarioId} tabKey="ar" period={period} selectedMonth={selectedMonth} customFrom={customFrom} customTo={customTo} fyStartMonth={fyStartMonth} currentMonth={currentMonth} />

            {/* ── 1. KPI Strip ── */}
            <div className="grid gap-3 md:grid-cols-6">
                <Card>
                    <CardHeader className="pb-1 pt-3 px-4">
                        <CardDescription className="text-xs">Total AR</CardDescription>
                        <CardTitle className="text-lg tabular-nums">{fmt(kpis.total_ar)}</CardTitle>
                    </CardHeader>
                    <CardContent className="px-4 pb-3">
                        <p className="text-[11px] text-muted-foreground">{kpis.invoice_count} invoices</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-1 pt-3 px-4">
                        <CardDescription className="text-xs">Overdue</CardDescription>
                        <CardTitle className={`text-lg tabular-nums ${overduePctColor(kpis.overdue_pct)}`}>
                            {fmt(kpis.overdue_total)}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="px-4 pb-3">
                        <p className={`text-[11px] font-medium ${overduePctColor(kpis.overdue_pct)}`}>{kpis.overdue_pct}% of total</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-1 pt-3 px-4">
                        <CardDescription className="text-xs">DSO</CardDescription>
                        <CardTitle className="text-lg tabular-nums">
                            {kpis.dso ? `${kpis.dso}d` : "\u2014"}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="px-4 pb-3">
                        <p className="text-[11px] text-muted-foreground">Trailing 90d revenue</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-1 pt-3 px-4">
                        <CardDescription className="text-xs">Collection Rate</CardDescription>
                        <CardTitle className={`text-lg tabular-nums ${collectionRateColor(kpis.collection_rate)}`}>
                            {kpis.collection_rate}%
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="px-4 pb-3">
                        <p className="text-[11px] text-muted-foreground">Last 30 days</p>
                        <p className="text-[10px] text-muted-foreground/60 mt-0.5">Benchmark: 80–95%</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-1 pt-3 px-4">
                        <CardDescription className="text-xs">Avg Days to Pay</CardDescription>
                        <CardTitle className="text-lg tabular-nums">
                            {kpis.weighted_avg_days_to_pay ? `${kpis.weighted_avg_days_to_pay}d` : "\u2014"}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="px-4 pb-3">
                        <p className="text-[11px] text-muted-foreground">Weighted, 6mo</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-1 pt-3 px-4">
                        <CardDescription className="text-xs">Top 10 Concentration</CardDescription>
                        <CardTitle className="text-lg tabular-nums">
                            {concentration.top_10_pct}%
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="px-4 pb-3">
                        <p className={`text-[11px] font-medium ${hhi.color}`}>HHI {Math.round(concentration.hhi).toLocaleString()} - {hhi.text}</p>
                    </CardContent>
                </Card>
            </div>

            {/* ── 2. Hero: AR Aging ── */}
            <Card>
                <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                        <ClockIcon className="size-5 text-[#209DB3]" />
                        <CardTitle className="text-base">Aged Receivables</CardTitle>
                    </div>
                </CardHeader>
                <CardContent>
                    {agingBarPatched ? (
                        <EChartsCard option={agingBarPatched} height={200} />
                    ) : (
                        <p className="text-sm text-muted-foreground text-center py-8">No aging data</p>
                    )}
                </CardContent>
            </Card>

            {/* ── 3. Secondary: Monthly Collection Trend ── */}
            {monthlyComboOption && (
                <Card>
                    <CardHeader className="pb-3">
                        <div className="flex items-center gap-2">
                            <TrendingUpIcon className="size-5 text-[#209DB3]" />
                            <CardTitle className="text-base">Collections Trend</CardTitle>
                        </div>
                        <CardDescription>Trailing 6 months — invoiced vs collected with collection rate</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <EChartsCard option={monthlyComboOption} height={280} />
                    </CardContent>
                </Card>
            )}

            {/* ── 4. Concentration bar (compact) ── */}
            {concentration.top_1_pct > 0 && (
                <Card>
                    <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <UsersIcon className="size-4 text-[#209DB3]" />
                                <CardTitle className="text-sm">Customer Concentration</CardTitle>
                            </div>
                            <span className={`text-xs font-medium ${hhi.color}`}>
                                HHI {Math.round(concentration.hhi).toLocaleString()} — {hhi.text}
                            </span>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-2">
                        <div className="flex h-5 rounded-full overflow-hidden">
                            <div
                                className="bg-red-500 transition-all"
                                style={{width: `${concentration.top_1_pct}%`}}
                                title={`Top 1: ${concentration.top_1_pct}%`}
                            />
                            <div
                                className="bg-orange-400 transition-all"
                                style={{width: `${Math.max(concentration.top_5_pct - concentration.top_1_pct, 0)}%`}}
                                title={`Next 4: ${(concentration.top_5_pct - concentration.top_1_pct).toFixed(1)}%`}
                            />
                            <div
                                className="bg-yellow-300 transition-all"
                                style={{width: `${Math.max(concentration.top_10_pct - concentration.top_5_pct, 0)}%`}}
                                title={`Next 5: ${(concentration.top_10_pct - concentration.top_5_pct).toFixed(1)}%`}
                            />
                            <div
                                className="bg-green-300 transition-all flex-1"
                                title={`Rest: ${(100 - concentration.top_10_pct).toFixed(1)}%`}
                            />
                        </div>
                        <div className="flex justify-between text-[11px] text-muted-foreground">
                            <span>Top 1: <strong>{concentration.top_1_pct}%</strong></span>
                            <span>Top 5: <strong>{concentration.top_5_pct}%</strong></span>
                            <span>Top 10: <strong>{concentration.top_10_pct}%</strong></span>
                            <span>Rest: <strong>{(100 - concentration.top_10_pct).toFixed(1)}%</strong></span>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* ── 5. Action tables: Top Customers + Past Due ── */}
            {topCustomers.length > 0 && (
                <Card>
                    <CardHeader className="pb-3">
                        <div className="flex items-center gap-2">
                            <UsersIcon className="size-5 text-[#209DB3]" />
                            <CardTitle className="text-base">Top Customers by Outstanding AR</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>#</TableHead>
                                    <TableHead>Customer</TableHead>
                                    <TableHead>Segment</TableHead>
                                    <TableHead className="text-right">Amount Due</TableHead>
                                    <TableHead className="text-right">Invoices</TableHead>
                                    <TableHead className="text-right">Overdue</TableHead>
                                    <TableHead className="text-right">Overdue %</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {topCustomers.map((c, i) => (
                                    <TableRow key={c.parent_name}>
                                        <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                                        <TableCell className="font-medium">{c.parent_name}</TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className="text-xs">
                                                {c.segment}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right tabular-nums">{fmt(c.total_due)}</TableCell>
                                        <TableCell className="text-right tabular-nums">{c.invoice_count}</TableCell>
                                        <TableCell className="text-right tabular-nums">{fmt(c.overdue_amount)}</TableCell>
                                        <TableCell className={`text-right tabular-nums ${overduePctColor(c.overdue_pct)}`}>
                                            {c.overdue_pct}%
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}

            {pastDue.length > 0 && (
                <Card>
                    <CardHeader className="pb-3">
                        <div className="flex items-center gap-2">
                            <AlertTriangleIcon className="size-5 text-[#209DB3]" />
                            <CardTitle className="text-base">Past Due Invoices</CardTitle>
                        </div>
                        <CardDescription>Top 25 by days overdue</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Customer</TableHead>
                                    <TableHead>Invoice #</TableHead>
                                    <TableHead className="text-right">Amount Due</TableHead>
                                    <TableHead className="text-right">Due Date</TableHead>
                                    <TableHead className="text-right">Days Overdue</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {pastDue.map((inv) => (
                                    <TableRow key={`${inv.invoice_number}-${inv.due_date}`}>
                                        <TableCell className="font-medium">{inv.contact_name}</TableCell>
                                        <TableCell className="text-muted-foreground">{inv.invoice_number || "\u2014"}</TableCell>
                                        <TableCell className="text-right tabular-nums">{fmt(inv.amount_due)}</TableCell>
                                        <TableCell className="text-right tabular-nums">{inv.due_date}</TableCell>
                                        <TableCell className={`text-right tabular-nums ${daysOverdueColor(inv.days_overdue)}`}>
                                            {inv.days_overdue}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
